import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useForm } from 'react-hook-form'

import { useSoundEffects } from '@/shared/hooks/useSoundEffects'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { useReloadGuard } from '@/shared/version'

import { deriveControlState, resolveAnswerSubmission } from './answerFlow'
import { SCREENS, resolveScreen, type Screen } from './screens'
import {
  findStepIndexById,
  hasPlayableStep,
  resolveNextStep,
  resolvePrevStep,
  toDisplayStep
} from './stepNavigation'

import type { LevelPlayAdapter } from './adapters/types'
import type { Step } from './stepTypes'

/**
 * Motor de execução do Level Play, compartilhado por jornada e quiz.
 *
 * É deliberadamente **cola fina**: toda decisão vive nos módulos puros ao lado
 * (`stepNavigation`, `screens`, `answerFlow`), que têm teste. O que sobra aqui
 * é o que não dá para testar no vitest deste projeto (`environment: 'node'`) —
 * estado do React, efeitos, formulário e chamada de rede.
 *
 * Se você for adicionar uma regra aqui, pergunte antes se ela não pertence a um
 * dos módulos puros. Quase sempre pertence.
 */

/** Índice do primeiro step que é de fato um desafio — o 0 é o `step-initial`. */
const FIRST_CHALLENGE_STEP_INDEX = 1

export interface UseLevelPlayInput {
  adapter: LevelPlayAdapter
  /**
   * Efeito de domínio ao concluir a execução — CSAT na jornada, por exemplo.
   * Fica fora do adapter de propósito: o adapter cobre carregamento e
   * finalização de passo (D4), não ciclo de vida.
   */
  onFinish?: () => void
  /**
   * Muda quando o recurso é (re)carregado. O motor reinicia a execução a cada
   * valor novo — equivale ao `useEffect([levelRaw])` da jornada.
   */
  resetKey?: string | number | null
}

export const useLevelPlay = ({
  adapter,
  onFinish,
  resetKey
}: UseLevelPlayInput) => {
  const { playSoundEffect, SOUNDS } = useSoundEffects()
  const { refetchProgress } = useUserProgress()

  const activityCardRef = useRef<HTMLDivElement>(null)
  const countdownRef = useRef<{ start: () => void; pause: () => void }>(null)

  const [hearts, setHearts] = useState<number | null>(adapter.livesLimit)
  const [currentScreen, setCurrentScreen] = useState<Screen>(
    SCREENS.INITIAL_SCREEN
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [answerIsWrong, setAnswerIsWrong] = useState(false)
  const [answerIsRight, setAnswerIsRight] = useState<boolean | undefined>(
    undefined
  )
  /** Fonte única da posição, base zero — a convenção do motor. */
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [stepsCustomFooters, _setStepsCustomFooters] = useState<
    Record<string, React.ReactNode>
  >({})
  /**
   * `activityId`s que já tiveram `finishStep` resolvido nesta execução. Vive na
   * execução (recurso + tentativa), não na rodada de vidas: o "Tentar mais uma
   * vez" da jornada o preserva — restaurar vidas não apaga submissões já
   * gravadas no servidor. Quem zera é o `clearSubmitted`, consumido por troca
   * de recurso, restart de quiz (tentativa nova) e resets de domínio.
   */
  const [submittedActivities, setSubmittedActivities] = useState<
    ReadonlySet<number>
  >(new Set())

  const methods = useForm()
  const answer = methods.watch('answer')

  const { answerIsEmpty, disableInputs, disableControls } = deriveControlState({
    answer,
    isSubmitting,
    answerIsWrong,
    answerIsRight
  })

  const steps: Step[] = adapter.stepSource.steps
  const totalSteps = steps.length
  const currentStep = toDisplayStep(currentStepIndex)

  /**
   * Tudo submetido é medido por `activityId`, não por step: intro e slides
   * intermediários nunca submetem, mas dividem `activityId` com a atividade
   * que submete — o conjunto de ids distintos dos steps jogáveis é o conjunto
   * das atividades.
   */
  const allSubmitted = useMemo(() => {
    const ids = new Set<number>()

    for (const step of steps) {
      if (step.isInitial || step.activityId == null) continue
      ids.add(step.activityId)
    }

    return (
      ids.size > 0 &&
      Array.from(ids).every(id => submittedActivities.has(id))
    )
  }, [steps, submittedActivities])

  const clearSubmitted = useCallback(
    () => setSubmittedActivities(new Set()),
    []
  )

  const resetAnswers = useCallback(() => {
    setAnswerIsRight(undefined)
    setAnswerIsWrong(false)
  }, [])

  const setStepsCustomFooters = useCallback(
    (stepId: string, customFooter: React.ReactNode) => {
      _setStepsCustomFooters(prevState => ({
        ...prevState,
        [stepId]: customFooter
      }))
    },
    []
  )

  const pauseCountdown = useCallback(() => countdownRef.current?.pause(), [])
  const startCountdown = useCallback(() => countdownRef.current?.start(), [])

  /**
   * O desconto também é chamado direto pelos jogos internos (Memory, WordHunt)
   * via surface, sem passar por `resolveAnswerSubmission` — a guarda contra
   * recurso sem vidas mora aqui e não só na resolução da resposta.
   */
  const loseHeart = useCallback(() => {
    if (adapter.livesLimit == null) return
    setHearts(prev => (prev == null ? prev : Math.max(prev - 1, 0)))
  }, [adapter.livesLimit])

  const goToOutOfTimeScreen = useCallback(
    () => setCurrentScreen(SCREENS.OUT_OF_TIME_SCREEN),
    []
  )

  // `resolveScreen` já leva para cá quando os corações zeram. Este setter existe
  // para quem precisa forçar a tela sem passar pelo estado — faz parte da
  // superfície que os consumidores já usavam.
  const goToOutOfHeartsScreen = useCallback(
    () => setCurrentScreen(SCREENS.OUT_OF_HEARTS_SCREEN),
    []
  )

  const goToFinishScreen = useCallback(() => {
    playSoundEffect(SOUNDS.LEVEL_COMPLETE)
    setCurrentScreen(SCREENS.FINISH_SCREEN)
    onFinish?.()
  }, [playSoundEffect, SOUNDS, onFinish])

  /**
   * Navega para um índice **base zero**. Diferente do `goToStep` da jornada,
   * que recebia base um e gravava `stepIndex - 1` — a conversão vivia no
   * chamador e era a origem do off-by-one do quiz.
   */
  const goToStepIndex = useCallback(
    (index: number) => {
      if (!totalSteps) return

      setCurrentStepIndex(index)
      if (index > 0) setCurrentScreen(SCREENS.STEPS_SCREEN)
    },
    [totalSteps]
  )

  const goToFirstActivityStep = useCallback(
    () => goToStepIndex(FIRST_CHALLENGE_STEP_INDEX),
    [goToStepIndex]
  )

  const goToNextStep = useCallback(() => {
    const resolution = resolveNextStep({ currentStepIndex, totalSteps })

    if (resolution.kind === 'noop') return
    if (resolution.kind === 'finish') {
      goToFinishScreen()
      resetAnswers()
      return
    }

    goToStepIndex(resolution.index)
    resetAnswers()
  }, [
    currentStepIndex,
    totalSteps,
    goToFinishScreen,
    goToStepIndex,
    resetAnswers
  ])

  const goToPrevStep = useCallback(() => {
    const resolution = resolvePrevStep({ currentStepIndex, totalSteps })

    if (resolution.kind !== 'step') return

    goToStepIndex(resolution.index)
  }, [currentStepIndex, totalSteps, goToStepIndex])

  const goToStepByActivityId = useCallback(
    (activityId: number | string) => {
      const index = findStepIndexById(steps, activityId)

      if (index !== -1) goToStepIndex(index)
    },
    [steps, goToStepIndex]
  )

  const onRestart = useCallback(() => {
    setCurrentScreen(SCREENS.INITIAL_SCREEN)
    setHearts(adapter.livesLimit)
    setCurrentStepIndex(0)
    resetAnswers()
  }, [adapter.livesLimit, resetAnswers])

  /**
   * Recomeço de execução **sem** voltar à tela inicial — o que os resets de
   * domínio (`onLevelReset`/`onQuizReset`) precisam: eles navegam para o
   * primeiro step em vez da abertura. Zera vidas, respostas e o conjunto de
   * submetidos, porque o progresso no servidor acabou de ser apagado.
   */
  const resetExecution = useCallback(() => {
    setHearts(adapter.livesLimit)
    setCurrentStepIndex(0)
    resetAnswers()
    clearSubmitted()
  }, [adapter.livesLimit, resetAnswers, clearSubmitted])

  const onAnswer = useCallback(
    async ({
      uid,
      answer: answerValue,
      answerIsCorrect = false,
      skipValidation = false,
      voucherId
    }: {
      uid: string
      answer?: unknown
      answerIsCorrect?: boolean
      skipValidation?: boolean
      voucherId?: number
    }) => {
      const resolution = resolveAnswerSubmission({
        isSubmitting,
        answerIsCorrect,
        skipValidation,
        allowWrongAnswers: adapter.allowWrongAnswers,
        livesLimit: adapter.livesLimit
      })

      if (resolution.kind === 'ignored') return

      setAnswerIsRight(resolution.answerIsRight)

      if (resolution.sound) {
        playSoundEffect(
          resolution.sound === 'correct'
            ? SOUNDS.ANSWER_CORRECT
            : SOUNDS.ANSWER_WRONG
        )
      }

      if (resolution.markWrong) setAnswerIsWrong(true)
      if (resolution.loseHeart) loseHeart()

      // Errou sem permissão de avançar: o feedback já foi dado, mas a resposta
      // não vai ao backend.
      if (resolution.kind === 'blocked') return

      setIsSubmitting(true)
      pauseCountdown()

      try {
        await adapter.finishStep({
          uid,
          payload: voucherId
            ? { kind: 'voucher', voucherId }
            : {
                kind: 'answer',
                answer: answerValue,
                answerIsRight: resolution.answerIsRight
              }
        })

        const activityId = steps.find(step => step.uid === uid)?.activityId
        if (activityId != null) {
          setSubmittedActivities(prev => new Set(prev).add(activityId))
        }

        // A retomada (jornada) lê `_lastUnfinishedChallenge` do progresso — um
        // refetch em voo no "Tentar mais uma vez" deixaria a janela aberta para
        // reenviar a resposta que acabou de submeter e sobrescrevê-la.
        await refetchProgress()
      } catch {
        startCountdown()
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      isSubmitting,
      adapter,
      playSoundEffect,
      SOUNDS,
      loseHeart,
      pauseCountdown,
      startCountdown,
      refetchProgress,
      steps
    ]
  )

  const onVerifyAnswer = useCallback(
    async (input: { uid: string; answer?: unknown; answerIsCorrect?: boolean }) =>
      onAnswer(input),
    [onAnswer]
  )

  const onComplete = useCallback(
    ({ answer: answerValue }: { answer: unknown }) => {
      methods.setValue('answer', answerValue)
      setAnswerIsRight(true)
    },
    [methods]
  )

  const onAdvance = useCallback(() => {
    resetAnswers()
    methods.setValue('answer', '')
    goToNextStep()
  }, [resetAnswers, methods, goToNextStep])

  const onContinue = useCallback(
    async ({ uid, answer: answerValue }: { uid: string; answer?: unknown }) => {
      await onAnswer({ uid, answer: answerValue, skipValidation: true })
      onAdvance()
    },
    [onAnswer, onAdvance]
  )

  const onRetry = useCallback(
    ({ keepAnswer = false }: { keepAnswer?: boolean } = {}) => {
      setAnswerIsWrong(false)
      if (!keepAnswer) methods.setValue('answer', '')
    },
    [methods]
  )

  /**
   * Enquanto o participante está numa etapa, nenhuma atualização de versão
   * recarrega a página. É o cenário concreto do incidente do PR #85: reload no
   * meio da execução derruba progresso que não volta.
   *
   * O engine é compartilhado por quiz e jornada, então uma linha aqui cobre os
   * dois. `UpdatePolicy` não sabe o que é 'level-play' — só conta razões.
   */
  useReloadGuard(
    currentScreen === SCREENS.STEPS_SCREEN && currentStepIndex > 0,
    'level-play'
  )

  /**
   * Transições derivadas — a precedência vive em `resolveScreen`, não aqui. A
   * conclusão derivada (última submissão com corações zerados) passa por
   * `goToFinishScreen` para tocar o som e disparar o `onFinish` de domínio —
   * `setCurrentScreen` direto concluiria em silêncio e sem CSAT.
   */
  useEffect(() => {
    const next = resolveScreen({
      currentScreen,
      hearts,
      currentStepIndex,
      allSubmitted
    })

    if (next === SCREENS.FINISH_SCREEN) goToFinishScreen()
    else if (next) setCurrentScreen(next)
  }, [currentScreen, hearts, currentStepIndex, allSubmitted, goToFinishScreen])

  // Reiniciar é reação ao recurso trocar, não à identidade da callback. Ler
  // `onRestart` de uma ref mantém o efeito keyed só em `resetKey` sem deixar a
  // callback envelhecer — incluí-la nas deps reiniciaria a execução sempre que
  // `livesLimit` mudasse de identidade. A troca de recurso também zera o
  // conjunto de submetidos: ele pertence à execução anterior.
  const onRestartRef = useRef(onRestart)
  const clearSubmittedRef = useRef(clearSubmitted)
  onRestartRef.current = onRestart
  clearSubmittedRef.current = clearSubmitted

  /** Recurso (re)carregado: reinicia a execução. */
  useEffect(() => {
    if (resetKey === null || resetKey === undefined) return

    clearSubmittedRef.current()
    onRestartRef.current()
  }, [resetKey])

  /**
   * Fonte assíncrona que terminou sem step nenhum significa nada a jogar — o
   * quiz fazia isso dentro do `loadQuestions`.
   */
  useEffect(() => {
    const source = adapter.stepSource

    if (source.kind !== 'async') return
    if (source.isLoading || !source.isExhausted) return
    // `hasPlayableStep`, não `steps.length`: a lista sempre traz o step inicial.
    if (hasPlayableStep(source.steps)) return

    goToFinishScreen()
  }, [adapter.stepSource, goToFinishScreen])

  return useMemo(
    () => ({
      activityCardRef,
      countdownRef,

      hearts,
      loseHeart,
      startCountdown,
      pauseCountdown,

      isSubmitting,
      setIsSubmitting,
      disableControls,
      disableInputs,
      answerIsWrong,
      answerIsRight,
      answerIsEmpty,
      methods,

      currentScreen,
      goToFinishScreen,
      goToOutOfTimeScreen,
      goToOutOfHeartsScreen,

      steps,
      totalSteps,
      currentStepIndex,
      currentStep,
      stepsCustomFooters,
      setStepsCustomFooters,

      goToStepIndex,
      goToFirstActivityStep,
      goToPrevStep,
      goToNextStep,
      goToStepByActivityId,

      onAnswer,
      onVerifyAnswer,
      onComplete,
      onAdvance,
      onContinue,
      onRetry,
      onRestart,

      submittedActivities,
      clearSubmitted,
      resetExecution,

      allowWrongAnswers: adapter.allowWrongAnswers,
      exitPath: adapter.exitPath()
    }),
    [
      hearts,
      loseHeart,
      startCountdown,
      pauseCountdown,
      isSubmitting,
      disableControls,
      disableInputs,
      answerIsWrong,
      answerIsRight,
      answerIsEmpty,
      methods,
      currentScreen,
      goToFinishScreen,
      goToOutOfTimeScreen,
      goToOutOfHeartsScreen,
      steps,
      totalSteps,
      currentStepIndex,
      currentStep,
      stepsCustomFooters,
      setStepsCustomFooters,
      goToStepIndex,
      goToFirstActivityStep,
      goToPrevStep,
      goToNextStep,
      goToStepByActivityId,
      onAnswer,
      onVerifyAnswer,
      onComplete,
      onAdvance,
      onContinue,
      onRetry,
      onRestart,
      submittedActivities,
      clearSubmitted,
      resetExecution,
      adapter
    ]
  )
}

export type LevelPlayState = ReturnType<typeof useLevelPlay>
